import { CollapsibleSection } from "components/ui";
import {
  TranslationTabs,
  TranslationValues,
} from "components/common/TranslationTabs";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { CHAT_LABEL_MAX_LENGTHS, ChatLabels } from "services/widgetService";

/**
 * One editor field per chat label, in display order. `key` is the label name stored in
 * `ChatLabels`; `i18nKey` is its block under `app.widgets.ux.chatLabels`. Adding a label is
 * one entry here, its max length in `CHAT_LABEL_MAX_LENGTHS`, and the widget's fallback.
 */
const LABEL_FIELDS: {
  key: keyof typeof CHAT_LABEL_MAX_LENGTHS;
  i18nKey: string;
}[] = [
  { key: "kbCitation", i18nKey: "kbCitationLabel" },
  { key: "inviteAgent", i18nKey: "inviteAgentLabel" },
  { key: "handoffConnecting", i18nKey: "handoffConnecting" },
  { key: "handoffWaiting", i18nKey: "handoffWaiting" },
  { key: "handoffAgentJoined", i18nKey: "handoffAgentJoined" },
  { key: "handoffAgentName", i18nKey: "handoffAgentName" },
  { key: "handoffMessageQueued", i18nKey: "handoffMessageQueued" },
  { key: "handoffEndChat", i18nKey: "handoffEndChat" },
  { key: "handoffPlaceholder", i18nKey: "handoffPlaceholder" },
  { key: "handoffEnded", i18nKey: "handoffEnded" },
  { key: "handoffFailed", i18nKey: "handoffFailed" },
];

/** Drop fields the editor left undefined so the stored map holds strings only. */
const toLabelMap = (values: TranslationValues): Record<string, string> =>
  Object.fromEntries(
    Object.entries(values).filter(
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  );

interface ChatLabelsSectionProps {
  chatLabels: ChatLabels;
  onChatLabelsChange: (labels: ChatLabels) => void;
}

/**
 * Brandable text the widget shows inside the chat: the badge on answers drawn from
 * knowledge-base documents ("Verified" by default), the buttons that invite a human agent
 * ("Invite an agent" by default), and the statuses and notices of a chat with your team.
 * Leaving a field empty keeps the built-in label, which the widget translates into the
 * visitor's language.
 */
export const ChatLabelsSection = ({
  chatLabels,
  onChatLabelsChange,
}: ChatLabelsSectionProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <CollapsibleSection
      pre={t("app.widgets.ux.chatLabels.pre")}
      title={t("app.widgets.ux.chatLabels.sectionTitle")}
      description={t("app.widgets.ux.chatLabels.sectionDescription")}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <TranslationTabs
        fields={LABEL_FIELDS.map(({ key, i18nKey }) => ({
          key,
          label: t(`app.widgets.ux.chatLabels.${i18nKey}.label`),
          type: "input",
          maxLength: CHAT_LABEL_MAX_LENGTHS[key],
          placeholder: t(`app.widgets.ux.chatLabels.${i18nKey}.placeholder`),
          helperText: t(`app.widgets.ux.chatLabels.${i18nKey}.helperText`),
        }))}
        defaultValues={chatLabels.default ?? {}}
        onDefaultChange={(values) =>
          onChatLabelsChange({ ...chatLabels, default: toLabelMap(values) })
        }
        translations={chatLabels.translations ?? {}}
        onTranslationsChange={(next) =>
          onChatLabelsChange({
            ...chatLabels,
            translations: Object.fromEntries(
              Object.entries(next).map(([lang, values]) => [
                lang,
                toLabelMap(values),
              ]),
            ),
          })
        }
      />
    </CollapsibleSection>
  );
};
